/**
 * Grudge ID auth helpers for Poker Grudge (fleet SSOT).
 * Identity before wallet — JWT required for Railway GBUX / wallet APIs.
 */
import { FLEET, FLEET_AUTH_TOKEN_KEYS } from "@shared/fleet";

const COOKIE_NAMES = ["grudge_auth_token", "sso_token"] as const;
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"),
  );
  if (!m?.[1]) return null;
  try {
    const v = decodeURIComponent(m[1]);
    return v.length > 10 ? v : null;
  } catch {
    return m[1].length > 10 ? m[1] : null;
  }
}

function writeFleetCookies(token: string | null): void {
  if (typeof document === "undefined") return;
  const host = typeof location !== "undefined" ? location.hostname : "";
  const domain =
    host.endsWith("grudge-studio.com") || host.endsWith("grudge.studio")
      ? "; Domain=.grudge-studio.com"
      : "";
  const base = `; path=/; max-age=${token ? COOKIE_MAX_AGE : 0}; SameSite=Lax; Secure${domain}`;
  for (const name of COOKIE_NAMES) {
    document.cookie = token
      ? `${name}=${encodeURIComponent(token)}${base}`
      : `${name}=; path=/; max-age=0; SameSite=Lax${domain}`;
  }
}

export function getGrudgeToken(): string | null {
  if (typeof window === "undefined") return null;
  for (const key of FLEET_AUTH_TOKEN_KEYS) {
    try {
      const v = localStorage.getItem(key);
      if (v && v.length > 10) return v;
    } catch {
      /* ignore */
    }
  }
  for (const name of COOKIE_NAMES) {
    const c = readCookie(name);
    if (c) {
      setGrudgeToken(c);
      return c;
    }
  }
  return null;
}

export function setGrudgeToken(token: string): void {
  try {
    localStorage.setItem("grudge.open.token", token);
    localStorage.setItem("grudge_auth_token", token);
    localStorage.setItem("grudge_session_token", token);
    localStorage.setItem("sso_token", token);
    localStorage.setItem("grudge_token", token);
  } catch {
    /* ignore */
  }
  writeFleetCookies(token);
}

export function clearGrudgeToken(): void {
  for (const key of FLEET_AUTH_TOKEN_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  writeFleetCookies(null);
}

/** Capture query or hash SSO from ID / wallet.grudge-studio.com handoff */
export function captureSsoTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const q =
    url.searchParams.get("grudge_token") ||
    url.searchParams.get("sso_token") ||
    url.searchParams.get("token") ||
    url.searchParams.get("grudge.open.token");
  if (q) {
    setGrudgeToken(q);
    url.searchParams.delete("grudge_token");
    url.searchParams.delete("sso_token");
    url.searchParams.delete("token");
    url.searchParams.delete("grudge.open.token");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    return q;
  }
  const hash = url.hash.replace(/^#/, "");
  if (
    hash.includes("grudge_token=") ||
    hash.includes("sso_token=") ||
    hash.includes("access_token=")
  ) {
    const params = new URLSearchParams(hash);
    const t =
      params.get("sso_token") ||
      params.get("grudge_token") ||
      params.get("access_token");
    if (t) {
      setGrudgeToken(t);
      window.history.replaceState({}, "", url.pathname + url.search);
      return t;
    }
  }
  return null;
}

/**
 * Canonical Grudge ID login — redirect_uri back to poker.
 * @see shared/fleet/authConnect.ts in GrudgeBuilder
 */
export function buildGrudgeLoginUrl(returnPath = "/lobby"): string {
  const redirect =
    typeof window !== "undefined"
      ? `${window.location.origin}${returnPath.startsWith("/") ? returnPath : `/${returnPath}`}`
      : `${FLEET.publicOrigin}${returnPath}`;
  const u = new URL("/login", FLEET.grudgeId);
  u.searchParams.set("redirect_uri", redirect);
  return u.toString();
}

/**
 * Admin-only gate on id.grudge-studio.com/admin (Puter + Grudge ID).
 * Does not replace player /login. Returns to poker /admin after allowlist check.
 */
export function buildGrudgeAdminLoginUrl(returnPath = "/admin"): string {
  const redirect =
    typeof window !== "undefined"
      ? `${window.location.origin}${returnPath.startsWith("/") ? returnPath : `/${returnPath}`}`
      : `${FLEET.publicOrigin}${returnPath.startsWith("/") ? returnPath : `/${returnPath}`}`;
  const u = new URL("/admin", FLEET.grudgeId);
  u.searchParams.set("redirect_uri", redirect);
  return u.toString();
}

export function loginWithGrudgeId(returnPath = "/lobby"): void {
  if (isLoggedIn()) return;
  window.location.href = buildGrudgeLoginUrl(returnPath);
}

/** Send operators to id.grudge-studio.com/admin (not player login). */
export function loginWithGrudgeAdmin(returnPath = "/admin"): void {
  window.location.href = buildGrudgeAdminLoginUrl(returnPath);
}

/** Capture puter_username / admin_gate from id admin handoff */
export function captureAdminHandoffFromUrl(): {
  puter?: string;
  adminGate?: boolean;
} {
  if (typeof window === "undefined") return {};
  const url = new URL(window.location.href);
  const puter =
    url.searchParams.get("puter_username") ||
    url.searchParams.get("puter") ||
    undefined;
  const adminGate =
    url.searchParams.get("admin_gate") === "1" ||
    url.searchParams.get("admin") === "1";
  if (puter || adminGate) {
    try {
      if (puter) {
        localStorage.setItem("poker_admin_puter", puter.toLowerCase());
        localStorage.setItem("poker_admin_handle", puter.toLowerCase());
      }
      if (adminGate) localStorage.setItem("poker_admin_gate", "1");
    } catch {
      /* ignore */
    }
    url.searchParams.delete("puter_username");
    url.searchParams.delete("puter");
    url.searchParams.delete("admin_gate");
    url.searchParams.delete("admin");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  }
  return { puter: puter || undefined, adminGate };
}

export function openGrudgeWalletSite(): void {
  const url = new URL(FLEET.walletSite);
  const tok = getGrudgeToken();
  if (tok) url.hash = "sso_token=" + encodeURIComponent(tok);
  window.open(url.toString(), "_blank", "noopener,noreferrer");
}

export function authHeaders(extra?: HeadersInit): Headers {
  const h = new Headers(extra);
  const token = getGrudgeToken();
  if (token) h.set("Authorization", `Bearer ${token}`);
  h.set("Content-Type", "application/json");
  return h;
}

export function isLoggedIn(): boolean {
  return Boolean(getGrudgeToken());
}
